import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Layout from '@/components/Layout';
import { Users, FileText, CheckCircle, Upload, MapPin, Play, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ token, user, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalCustomers: 0,
    totalDeliveryNotes: 0,
    signedNotes: 0,
    unsignedNotes: 0
  });
  const [activeTour, setActiveTour] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchActiveTour();
  }, []);

  const fetchActiveTour = async () => {
    try {
      const response = await axios.get(`${API}/tours`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const inProgressTour = response.data.find(t => t.status === 'in_progress');
      setActiveTour(inProgressTour);
    } catch (error) {
      console.error('Fehler beim Laden der aktiven Tour:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const [customersRes, notesRes] = await Promise.all([
        axios.get(`${API}/customers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/delivery-notes`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const customers = customersRes.data;
      const notes = notesRes.data;
      const signed = notes.filter(n => n.signed).length;

      setStats({
        totalCustomers: customers.length,
        totalDeliveryNotes: notes.length,
        signedNotes: signed,
        unsignedNotes: notes.length - signed
      });
    } catch (error) {
      console.error('Fehler beim Laden der Statistiken:', error);
    }
  };

  const statCards = [
    {
      title: 'Kunden',
      value: stats.totalCustomers,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      testId: 'stat-customers',
      onClick: () => navigate('/customers')
    },
    {
      title: 'Lieferscheine',
      value: stats.totalDeliveryNotes,
      icon: FileText,
      color: 'from-purple-500 to-purple-600',
      testId: 'stat-delivery-notes',
      onClick: () => navigate('/delivery-notes')
    },
    {
      title: 'Unterschrieben',
      value: stats.signedNotes,
      icon: CheckCircle,
      color: 'from-green-500 to-green-600',
      testId: 'stat-signed',
      onClick: () => navigate('/delivery-notes?filter=signed')
    },
    {
      title: 'Ausstehend',
      value: stats.unsignedNotes,
      icon: Upload,
      color: 'from-orange-500 to-orange-600',
      testId: 'stat-unsigned',
      onClick: () => navigate('/delivery-notes?filter=unsigned')
    }
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-8" data-testid="dashboard">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#0A1F33' }}>Dashboard</h1>
          <p className="text-gray-600">Willkommen zurück, {user?.username}!</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card 
                key={index} 
                className="glass-effect border-0 cursor-pointer hover:shadow-xl transition-all transform hover:scale-105" 
                data-testid={stat.testId}
                onClick={stat.onClick}
              >
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">{stat.title}</CardTitle>
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.color}`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold" style={{ color: '#0A1F33' }}>{stat.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="glass-effect border-0">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Schnellaktionen</CardTitle>
              <CardDescription>Häufig verwendete Funktionen</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full justify-start metaxon-primary text-white cursor-pointer hover:shadow-lg transition-all" 
                onClick={() => navigate('/customers')}
                data-testid="quick-action-customers"
              >
                <Users className="mr-2 h-4 w-4" />
                Kunden verwalten
              </Button>
              <Button 
                className="w-full justify-start metaxon-primary text-white cursor-pointer hover:shadow-lg transition-all" 
                onClick={() => navigate('/delivery-notes')}
                data-testid="quick-action-delivery-notes"
              >
                <FileText className="mr-2 h-4 w-4" />
                Lieferscheine
              </Button>
              <Button 
                className="w-full justify-start metaxon-primary text-white cursor-pointer hover:shadow-lg transition-all" 
                onClick={() => navigate('/tours')}
                data-testid="quick-action-tours"
              >
                <MapPin className="mr-2 h-4 w-4" />
                Touren planen
              </Button>
              <Button 
                className="w-full justify-start bg-gradient-to-br from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 cursor-pointer hover:shadow-lg transition-all" 
                onClick={() => navigate('/delivery-notes?action=upload')}
                data-testid="quick-action-upload"
              >
                <Upload className="mr-2 h-4 w-4" />
                Lieferschein hochladen
              </Button>
            </CardContent>
          </Card>

          {/* Active Tour Widget */}
          <Card className="glass-effect border-0">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Aktuelle Tour</CardTitle>
              <CardDescription>Ihr aktueller Lieferfortschritt</CardDescription>
            </CardHeader>
            <CardContent>
              {activeTour ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-lg">{activeTour.name}</h3>
                      <p className="text-sm text-gray-600">
                        {activeTour.stops.filter(s => s.completed).length} von {activeTour.stops.length} Stops erledigt
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-orange-600">
                        {Math.round((activeTour.stops.filter(s => s.completed).length / activeTour.stops.length) * 100)}%
                      </div>
                      <div className="text-xs text-gray-500">Fortschritt</div>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-orange-400 to-orange-600 h-3 rounded-full transition-all" 
                      style={{ 
                        width: `${(activeTour.stops.filter(s => s.completed).length / activeTour.stops.length) * 100}%` 
                      }}
                    ></div>
                  </div>

                  <Button 
                    className="w-full metaxon-primary text-white" 
                    onClick={() => navigate('/tours')}
                  >
                    <Play className="mr-2 h-4 w-4" />
                    Tour fortsetzen
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <MapPin className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500 mb-4">Keine aktive Tour</p>
                  <Button 
                    className="metaxon-primary text-white" 
                    onClick={() => navigate('/tours')}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Neue Tour erstellen
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
